import { useAccount } from "@/composables/account/useAccount";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { useAdminSession } from "@/composables/useAdminSession";
import { SOLDERA_SUPPORT_EMAIL } from "@/constants";
import { expectSavedCompany } from "@/signup/signup";
import type { CompanySaveResult } from "@/types";
import { inject, ref } from "vue";
import { useI18n } from "vue-i18n";

export interface CorporateCompanyData {
  name: string;
  registry_code: string;
  address: string;
  vat: string | null;
  country: string;
  phone_number: string;
  agreed_to_terms?: boolean;
}

export function useCorporateSignup() {
  const globalErrorHandler = inject("globalErrorHandler") as (
    error: any
  ) => void;
  const { t } = useI18n();

  const companyData = ref<CorporateCompanyData>({
    name: "",
    registry_code: "",
    address: "",
    vat: null,
    country: "",
    phone_number: "",
    agreed_to_terms: false,
  });

  const loading = ref(false);

  const accountStore = useAccountStore();
  const { accountData } = useAccount();
  const isAdmin = useAdminSession(() => accountData);

  const createCompany = async (
    data: CorporateCompanyData
  ): Promise<CompanySaveResult> => {
    loading.value = true;

    const payload: CorporateCompanyData = isAdmin.value
      ? (() => {
          const { agreed_to_terms: _, ...rest } = data;
          return rest;
        })()
      : data;

    try {
      return expectSavedCompany(
        await accountStore.postAccountChange(
          "/api/signup/corporate/company",
          payload
        )
      );
    } catch (err: any) {
      const responseData = err?.response?.data;
      if (responseData?.error_type === "COMPANY_CREATION_RATE_LIMITED") {
        // Backend caps company creation per day; surface its message (already localized to
        // the account language) instead of the generic failure so the user knows to contact
        // support rather than retry.
        const description =
          responseData?.error_data?.description ||
          t("signup.corporate.rate_limit_error", {
            supportEmail: SOLDERA_SUPPORT_EMAIL,
          });
        globalErrorHandler({ message: description, code: responseData.code });
      } else {
        globalErrorHandler(err);
      }
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    companyData,
    loading,
    isAdmin,
    createCompany,
  };
}
